import { useEffect, useRef, useState } from 'react';
import {
  PHOTO_ACCEPTED_MIME,
  PHOTO_MAX_BYTES,
} from '@camper-diary/shared';
import {
  listPoiPhotos,
  uploadPoiPhoto,
  deletePoiPhoto,
  type PoiPhoto,
} from '../api/poiPhotos';
import { HttpError } from '../api/client';

type Props = { poiId: string };

type FileStatus =
  | { state: 'queued' }
  | { state: 'uploading' }
  | { state: 'done' }
  | { state: 'error'; msg: string };

export function PoiPhotos({ poiId }: Props) {
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [photos, setPhotos] = useState<PoiPhoto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<Map<string, FileStatus>>(new Map());

  useEffect(() => {
    listPoiPhotos(poiId)
      .then(setPhotos)
      .catch((e: Error) => setLoadError(e.message ?? 'Errore caricamento foto'));
  }, [poiId]);

  function key(f: File): string {
    return `${f.name}:${f.size}:${f.lastModified}`;
  }

  function setFileStatus(k: string, s: FileStatus) {
    setStatuses((prev) => {
      const next = new Map(prev);
      next.set(k, s);
      return next;
    });
  }

  async function handleFiles(fileList: FileList | File[]) {
    const files = Array.from(fileList);
    if (files.length === 0) return;

    const initial = new Map(statuses);
    for (const f of files) initial.set(key(f), { state: 'queued' });
    setStatuses(initial);

    for (const f of files) {
      const k = key(f);
      if (f.size > PHOTO_MAX_BYTES) {
        setFileStatus(k, {
          state: 'error',
          msg: `File troppo grande (max ${PHOTO_MAX_BYTES / 1024 / 1024} MB)`,
        });
        continue;
      }
      setFileStatus(k, { state: 'uploading' });
      try {
        const photo = await uploadPoiPhoto(poiId, f);
        setFileStatus(k, { state: 'done' });
        setPhotos((prev) => (prev ? sortPhotos([...prev, photo]) : [photo]));
      } catch (err) {
        const msg =
          err instanceof HttpError
            ? err.apiError.message
            : err instanceof Error
              ? err.message
              : 'Errore sconosciuto';
        setFileStatus(k, { state: 'error', msg });
      }
    }
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) void handleFiles(e.target.files);
    if (e.target) e.target.value = '';
  }

  async function onDelete(p: PoiPhoto) {
    if (!confirm('Eliminare questa foto?')) return;
    try {
      await deletePoiPhoto(p.id);
      setPhotos((prev) => (prev ? prev.filter((x) => x.id !== p.id) : prev));
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Errore eliminazione');
    }
  }

  const entries = Array.from(statuses.entries());
  const inFlight = entries.filter(([, s]) => s.state === 'uploading' || s.state === 'queued');
  const errors = entries.filter(([, s]) => s.state === 'error');

  return (
    <div className="poi-photos">
      <div className="poi-photos-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => cameraInputRef.current?.click()}
        >
          📷 Scatta foto
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => galleryInputRef.current?.click()}
        >
          🖼️ Da galleria
        </button>
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={onInputChange}
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept={PHOTO_ACCEPTED_MIME.join(',')}
          multiple
          hidden
          onChange={onInputChange}
        />
      </div>
      <p className="muted small">
        JPEG, PNG, WebP, GIF · max 25 MB per foto
      </p>

      {inFlight.length > 0 && (
        <div className="upload-progress">⏳ {inFlight.length} in corso…</div>
      )}
      {errors.length > 0 && (
        <ul className="upload-errors">
          {errors.map(([k, s]) =>
            s.state === 'error' ? (
              <li key={k} className="err">
                {k.split(':')[0]}: {s.msg}
              </li>
            ) : null,
          )}
        </ul>
      )}

      {loadError && <p className="err">{loadError}</p>}
      {!photos && !loadError && <p className="muted">Caricamento foto…</p>}

      {photos && photos.length > 0 && (
        <ul className="photo-grid">
          {photos.map((p) => (
            <li key={p.id} className="photo-thumb">
              <img src={p.url} alt={p.caption ?? ''} loading="lazy" />
              <button
                type="button"
                className="thumb-delete"
                onClick={() => void onDelete(p)}
                aria-label="Elimina foto"
                title="Elimina foto"
              >
                ×
              </button>
              {p.caption && <span className="thumb-caption">{p.caption}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function sortPhotos(arr: PoiPhoto[]): PoiPhoto[] {
  return [...arr].sort((a, b) => {
    const aT = a.takenAt ?? a.createdAt;
    const bT = b.takenAt ?? b.createdAt;
    return aT.localeCompare(bT);
  });
}
